import axios from 'axios';

import type { ApiErrorItem } from '@/types/api';

/** jsdom Blob has no `.text()`; browsers do. */
async function readBlobBodyAsUtf8Text(data: object): Promise<string> {
  const blob = data as Blob;
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  if (typeof blob.arrayBuffer === 'function') {
    const buf = await blob.arrayBuffer();
    return new TextDecoder().decode(buf);
  }
  if (typeof FileReader !== 'undefined') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
      reader.readAsText(blob);
    });
  }
  return '';
}

async function httpErrorBodyToRecord(data: unknown): Promise<Record<string, unknown>> {
  if (data == null) return {};
  if (typeof data === 'object' && !(data instanceof Blob)) {
    return data as Record<string, unknown>;
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await readBlobBodyAsUtf8Text(data);
      if (!text?.trim()) return {};
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

function firstMessageFromOutputReportBody(data: Record<string, unknown>): string {
  const errors = data.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0] as { message?: string };
    if (typeof first?.message === 'string' && first.message) return first.message;
  }
  const top = data.message;
  return typeof top === 'string' ? top : '';
}

/**
 * Turns a failed output-report download (JSON or JSON-in-Blob) into a normal `Error`
 * with optional `errors` / `statusCode` for `handleOutputReportsExportError`.
 */
export async function normalizeOutputReportAxiosError(error: unknown): Promise<Error> {
  if (!axios.isAxiosError(error) || !error.response) {
    return error instanceof Error ? error : new Error(String(error));
  }
  const { status, data } = error.response;
  const record = await httpErrorBodyToRecord(data);
  let message = firstMessageFromOutputReportBody(record);
  if (!message) {
    message = `エラーが発生しました: ${status}`;
  }
  const err = new Error(message);
  const errors = record.errors;
  if (Array.isArray(errors)) {
    Object.assign(err, { errors: errors as ApiErrorItem[], statusCode: status });
  } else {
    Object.assign(err, { statusCode: status });
  }
  return err;
}
